import { inject, Injectable } from '@angular/core';
import {
  DataModel,
  InitService,
  MenuItem,
  RestService,
} from '@infodata-group/inf-commons';
import { ProductFactory } from '../factory/product-factory';
import { Product } from '../model/product';
import {
  BehaviorSubject,
  map,
  mergeMap,
  Observable,
  Observer,
  of,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { VersionService } from './version.service';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  public readonly initService = inject(InitService);
  private readonly restService = inject(RestService);
  private readonly versionService = inject(VersionService);

  private readonly changelogMenuItem: MenuItem = new MenuItem(
    'changelogs',
    'Changelogs',
    'gear-code',
    'changelogs',
  );
  public items: MenuItem[] = [this.changelogMenuItem];
  public productList: Product[];
  public model: DataModel;
  public isLoaded = false;
  private isLoading = false;
  protected _emitter$ = new Subject<boolean>();
  public requestedPage = '';

  private currentItems$$ = new BehaviorSubject<MenuItem[]>([]);
  public currentItems$ = this.currentItems$$.asObservable();
  public currentItem$ = this.currentItems$$.pipe(map((items) => items.at(-1)));

  public init(): void {
    if (this.isLoaded) {
      this._emitter$.next(true);
      return;
    }
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.loadSettings$()
      .pipe(
        mergeMap(() => this.generateMenu$()),
        mergeMap(() =>
          this.versionService
            .generateModel$()
            .pipe(tap((model) => (this.model = model))),
        ),
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.isLoaded = true;
          this._emitter$.next(true);
        },
        error: () => (this.isLoading = false),
      });
  }

  private loadSettings$(): Observable<boolean> {
    if (this.initService.isLoaded) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const subscription = this.initService.subscribe((event) => {
        if (event.name === 'settings') {
          observer.next(true);
          observer.complete();
        }
      });
      return () => subscription.unsubscribe();
    });
  }

  private generateMenu$() {
    return this.restService.getAll(new ProductFactory()).pipe(
      tap((productList) => {
        this.populateChangelogsMenu(productList);
        this.productList = productList;
      }),
    );
  }

  private populateChangelogsMenu(products: Product[]) {
    this.changelogMenuItem.submenus = products.map((p) => p.toMenuItem());
  }

  public setCurrentItems(items: MenuItem[]) {
    this.currentItems$$.next(items);
  }

  public findCurrentItems(route: string) {
    this.currentItems$$.next(this.pathByRoute(route));
  }

  public pathByRoute(route: string): MenuItem[] {
    const result = this.items
      .map((item) => this.findPathByRoute(item, route))
      .find((r) => r !== null);
    if (result) {
      return result.reverse();
    }
    return [];
  }

  public findPathByRoute(
    item: MenuItem,
    route: string,
    path: MenuItem[] = [],
  ): MenuItem[] {
    if (`/${item.route}` === route) {
      path.push(item);
      return path;
    }
    for (const subItem of item.submenus) {
      const found = this.findPathByRoute(subItem, route, path);
      if (found) {
        path.push(subItem);
        return path;
      }
    }
    return null;
  }

  public getFirstItem(): MenuItem[] {
    for (const item of this.changelogMenuItem.submenus) {
      if (item.route) {
        return [item];
      }
      if (item.hasSubMenus()) {
        for (const subItem of item.submenus) {
          if (subItem.route) {
            return [item, subItem];
          }
        }
      }
    }
    return [];
  }

  public subscribe(observer: Partial<Observer<boolean>>): Subscription {
    return this._emitter$.subscribe(observer);
  }
}
